import { Injectable } from '@angular/core';
import { jwtDecode } from 'jwt-decode';
import { UserDataClaimDTO } from '../models/user-data-claim.dto';

@Injectable({
  providedIn: 'root',
})
export class TokenStorageService {
  private readonly ACCESS_TOKEN_KEY = 'access_token';
  private readonly REFRESH_TOKEN_KEY = 'refresh_token';
  private readonly ACCOUNT_ID_KEY = 'account_id';
  private readonly TOKEN_ID_KEY = 'jwt_token_id';
  private readonly ROLE_KEY = 'jwt_role';
  private readonly ACCOUNT_FIRST_NAME_KEY = 'account_first_name';
  private readonly ACCOUNT_LAST_NAME_KEY = 'account_last_name';

  saveAccessToken(token: string): void {
    localStorage.setItem(this.ACCESS_TOKEN_KEY, token);
  }

  getAccessToken(): string | null {
    return localStorage.getItem(this.ACCESS_TOKEN_KEY);
  }

  saveRefreshToken(token: string): void {
    localStorage.setItem(this.REFRESH_TOKEN_KEY, token);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  saveAccountId(accountId: number): void {
    localStorage.setItem(this.ACCOUNT_ID_KEY, accountId.toString());
  }

  getAccountId(): number | null {
    const accountId = localStorage.getItem(this.ACCOUNT_ID_KEY);
    return accountId ? parseInt(accountId, 10) : null;
  }

  saveTokenId(tokenId: number): void {
    localStorage.setItem(this.TOKEN_ID_KEY, tokenId.toString());
  }

  getTokenId(): number | null {
    const tokenId = localStorage.getItem(this.TOKEN_ID_KEY);
    return tokenId ? parseInt(tokenId, 10) : null;
  }

  saveRole(role: string): void {
    localStorage.setItem(this.ROLE_KEY, role);
  }

  getRole(): string | null {
    return localStorage.getItem(this.ROLE_KEY);
  }

  saveAccountFirstName(firstName: string): void {
    localStorage.setItem(this.ACCOUNT_FIRST_NAME_KEY, firstName);
  }

  getAccountFirstName(): string | null {
    return localStorage.getItem(this.ACCOUNT_FIRST_NAME_KEY);
  }

  saveAccountLastName(lastName: string): void {
    localStorage.setItem(this.ACCOUNT_LAST_NAME_KEY, lastName);
  }

  getAccountLastName(): string | null {
    return localStorage.getItem(this.ACCOUNT_LAST_NAME_KEY);
  }

  getAllClaims(): any | null {
    const token = this.getAccessToken();
    if (!token) {
      return null;
    }

    try {
      const decoded: any = jwtDecode(token);
      return decoded;
    } catch (error) {
      console.error('Errore nella decodifica del token JWT:', error);
      return null;
    }
  }

  getUserData(): UserDataClaimDTO | null {
    const token = this.getAccessToken();
    if (!token) {
      return null;
    }

    try {
      const decoded: any = jwtDecode(token);
      // Il claim UserData è in formato: "http://schemas.microsoft.com/ws/2008/06/identity/claims/userdata"
      const userDataClaim = decoded['http://schemas.microsoft.com/ws/2008/06/identity/claims/userdata'] 
        || decoded['userdata'] 
        || decoded['UserData'];
      
      if (!userDataClaim) {
        return null;
      }

      // userDataClaim è una stringa JSON, quindi dobbiamo parsarla
      const userData: UserDataClaimDTO = typeof userDataClaim === 'string' 
        ? JSON.parse(userDataClaim) 
        : userDataClaim;

      // Aggiungi la proprietà derivata accountIsDisabled (inverso di AccountIsEnabled)
      if (userData.AccountIsEnabled !== undefined) {
        userData.accountIsDisabled = !userData.AccountIsEnabled;
      }

      return userData;
    } catch (error) {
      console.error('Errore nella decodifica del token JWT:', error);
      return null;
    }
  }

  /**
   * Estrae e salva tutti i claims dal token JWT nello storage
   * @returns true se l'account è abilitato, false se disabilitato o se c'è un errore
   */
  extractAndSaveClaimsFromToken(): boolean {
    const token = this.getAccessToken();
    if (!token) {
      return false;
    }

    try {
      const decoded: any = jwtDecode(token);
      
      // Estrai e salva il role
      const role = decoded['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
      if (role) {
        this.saveRole(role);
      }

      // Estrai e salva l'accountId (nameidentifier)
      const accountId = decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'];
      if (accountId) {
        const accountIdNum = parseInt(accountId, 10);
        if (!isNaN(accountIdNum)) {
          this.saveAccountId(accountIdNum);
        }
      }

      // Estrai e controlla userData per AccountIsEnabled
      const userDataClaim = decoded['http://schemas.microsoft.com/ws/2008/06/identity/claims/userdata'] 
        || decoded['userdata'] 
        || decoded['UserData'];
      
      if (userDataClaim) {
        const userData: UserDataClaimDTO = typeof userDataClaim === 'string' 
          ? JSON.parse(userDataClaim) 
          : userDataClaim;

        // Restituisci false se AccountIsEnabled è false (account disabilitato)
        if (userData.AccountIsEnabled === false) {
          return false;
        }
      }

      return true;
    } catch (error) {
      console.error('Errore nell\'estrazione dei claims dal token JWT:', error);
      return false;
    }
  }

  clear(): void {
    localStorage.removeItem(this.ACCESS_TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.ACCOUNT_ID_KEY);
    localStorage.removeItem(this.TOKEN_ID_KEY);
    localStorage.removeItem(this.ACCOUNT_FIRST_NAME_KEY);
    localStorage.removeItem(this.ACCOUNT_LAST_NAME_KEY);
  }
}
